import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Sparkles } from "lucide-react";
import { Logo, MonoLabel } from "../components/kit";
import { PricingPlans } from "../components/PricingPlans";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const TeaserRow = ({ label, value }) => (
  <div>
    <span className="font-mono-j text-[10px] tracking-[0.12em] text-[#54505B] uppercase block mb-1">{label}</span>
    <p className="text-[13.5px] text-[#181621] leading-snug blur-[7px] select-none" aria-hidden="true">{value}</p>
  </div>
);

const BrainTeaser = ({ brain }) => {
  const s = brain?.snapshot || {};
  return (
    <div className="relative card-soft p-6 sm:p-7 overflow-hidden" data-testid="brain-teaser-card">
      <div className="flex items-center justify-between gap-3">
        <MonoLabel>BRAND BRAIN — {(brain?.business_name || "YOUR BUSINESS").toUpperCase()}</MonoLabel>
        <span className="inline-flex items-center gap-1.5 bg-[#181621] text-[#FAF8F3] rounded-full px-3 py-1 font-mono-j text-[10px] tracking-[0.12em] shrink-0" data-testid="brain-teaser-lock-badge">
          <Lock size={11} strokeWidth={2} /> UNLOCKS WITH A PLAN
        </span>
      </div>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 pointer-events-none">
        <TeaserRow label="What you sell" value={s.what_they_sell || "Your offer, read and structured by the Brand Brain."} />
        <TeaserRow label="Who pays you" value={s.who_pays || "Your ideal customer, identified by the quiz."} />
        <TeaserRow label="Your niche" value={s.niche || "Your brand's precise positioning."} />
        <TeaserRow label="Recommended first move" value="Your first monthly strategy: channels, budget and next actions." />
      </div>
    </div>
  );
};

export default function ChoosePlan() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [busyPlan, setBusyPlan] = useState(null);
  const [brain, setBrain] = useState(null);

  useEffect(() => {
    api.get("/brand-brain").then((r) => setBrain(r.data)).catch(() => {});
  }, []);

  const select = async (plan, interval) => {
    if (plan === "agency") return;
    setBusyPlan(plan);
    try {
      await api.post("/billing/select-plan", { plan, interval });
      await refreshUser();
      toast.success(plan === "starter" ? "Starter plan activated — Brand Brain unlocked." : "7-day trial activated — no card required.");
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyPlan(null);
    }
  };

  return (
    <div className="min-h-screen dot-grid" data-testid="choose-plan-page">
      <div className="max-w-6xl w-full mx-auto px-6 py-6">
        <Logo className="h-5 sm:h-6" />
      </div>
      <div className="max-w-6xl mx-auto px-6 pb-24">
        <div className="inline-flex items-center gap-2 bg-[#E9F2E4] text-[#3E7B3E] rounded-full px-3.5 py-1.5 font-mono-j text-[10.5px] tracking-[0.14em]" data-testid="brain-ready-badge">
          <Sparkles size={12} strokeWidth={2} /> BRAND BRAIN BUILT
        </div>
        <h1 className="mt-4 text-3xl sm:text-4xl leading-tight" data-testid="choose-plan-headline">
          <span className="font-serif-it block">Your Brand Brain is ready.</span>
          <span className="font-extrabold tracking-[-0.02em] block">Pick a plan to generate your first strategy.</span>
        </h1>
        <p className="mt-3 text-[14px] text-[#54505B] max-w-lg">
          The quiz is done, your business is mapped. Founder and Pro start with a 7-day trial, no card — change or cancel anytime.
        </p>
        <div className="mt-8">
          <BrainTeaser brain={brain} />
        </div>
        <div className="mt-10">
          <PricingPlans
            onSelect={select}
            busyPlan={busyPlan}
            ctaLabel={(p) => (p.id === "starter" ? "Unlock for free" : "Try 7 days free")}
          />
        </div>
      </div>
    </div>
  );
}
